import { Link } from 'react-router-dom';
import {
  ArrowDownLeftIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  ArrowsLeftRightIcon,
  CoinIcon,
  HouseIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SignatureIcon,
  SparkleIcon,
} from '@phosphor-icons/react/dist/ssr';

import { ScreenShell } from '@/components/screen-shell';
import { Badge } from '@/components/ui/badge';
import { Card, CardEyebrow, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Variant = { label: string; query?: string; description?: string };
type Section = {
  route: string;
  title: string;
  tone: 'primary' | 'info' | 'warning' | 'danger' | 'success' | 'violet';
  Icon: typeof ArrowDownLeftIcon;
  variants: Variant[];
};

const SECTIONS: Section[] = [
  {
    route: '/home',
    title: 'Home',
    tone: 'primary',
    Icon: HouseIcon,
    variants: [
      { label: 'Default', description: 'Tela inicial pós-auth (address mockado)' },
    ],
  },
  {
    route: '/deploy-wallet',
    title: 'Deploy Wallet',
    tone: 'violet',
    Icon: RocketLaunchIcon,
    variants: [{ label: 'Default', description: 'Criação da deposit wallet' }],
  },
  {
    route: '/deposit',
    title: 'Deposit',
    tone: 'success',
    Icon: ArrowDownLeftIcon,
    variants: [
      { label: 'Default', description: 'Fluxo de depósito (saldo mockado)' },
    ],
  },
  {
    route: '/withdraw',
    title: 'Withdraw',
    tone: 'info',
    Icon: ArrowUpRightIcon,
    variants: [
      { label: 'Form', description: 'Formulário inicial (limite diário mockado)' },
    ],
  },
  {
    route: '/swap',
    title: 'Swap',
    tone: 'warning',
    Icon: ArrowsLeftRightIcon,
    variants: [
      { label: 'Default', description: 'USDC → pUSD via Polymarket' },
    ],
  },
  {
    route: '/approvals',
    title: 'Approvals',
    tone: 'danger',
    Icon: ShieldCheckIcon,
    variants: [
      { label: 'Default', description: 'Setup de permissões on-chain' },
    ],
  },
  {
    route: '/bridge',
    title: 'Bridge',
    tone: 'primary',
    Icon: CoinIcon,
    variants: [
      { label: 'Default', description: 'Bridge USDC para Polygon' },
    ],
  },
  {
    route: '/sign-order',
    title: 'Sign Order',
    tone: 'violet',
    Icon: SignatureIcon,
    variants: [
      {
        label: 'Confirm — BUY',
        query: 'orderId=dev-order',
        description: 'Resumo da ordem antes de assinar',
      },
      {
        label: 'Sem orderId (estado de erro)',
        description:
          'Renderiza "Missing orderId" — útil pra estilizar deeplink quebrado',
      },
    ],
  },
];

const TONE_CLASSES: Record<Section['tone'], string> = {
  primary: 'bg-primary/15 text-foreground ring-primary/30',
  info: 'bg-sky-500/12 text-sky-300 ring-sky-500/30',
  warning: 'bg-amber-500/12 text-amber-300 ring-amber-500/30',
  danger: 'bg-rose-500/12 text-rose-300 ring-rose-500/30',
  success: 'bg-emerald-500/12 text-emerald-300 ring-emerald-500/30',
  violet: 'bg-violet-500/15 text-violet-200 ring-violet-500/30',
};

function buildHref(route: string, extraQuery?: string): string {
  const params = new URLSearchParams(extraQuery ?? '');
  params.set('dev', '1');
  return `${route}?${params.toString()}`;
}

export default function DevIndex() {
  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <SparkleIcon weight="fill" className="size-3" />
          Dev preview
        </span>
      }
      title="Torian Mini App"
      description={
        <>
          Auth do Telegram/API/Web3Auth está bypassada (
          <code className="rounded bg-foreground/10 px-1 py-0.5 text-[12px]">
            VITE_DEV_PREVIEW
          </code>{' '}
          ou{' '}
          <code className="rounded bg-foreground/10 px-1 py-0.5 text-[12px]">
            ?dev=1
          </code>
          ). API mockada — botões funcionam e levam aos próximos estados.
        </>
      }
    >
      <Card>
        <p className="text-muted-foreground text-[13px] leading-relaxed">
          Estados <em>signing / submitting / submitted / error</em> aparecem ao
          clicar nos botões de cada tela (a API mock devolve sucesso instantâneo).
        </p>
      </Card>

      <div className="flex flex-col gap-3 sm:gap-4">
        {SECTIONS.map((section) => (
          <div
            key={section.route}
            className="flex flex-col gap-2"
            style={
              { '--fade-delay': `${SECTIONS.indexOf(section) * 0.04}s` } as React.CSSProperties
            }
          >
            <div className="flex items-center gap-2 pl-1">
              <span
                className={cn(
                  'inline-flex size-7 items-center justify-center rounded-xl ring-1',
                  TONE_CLASSES[section.tone],
                )}
              >
                <section.Icon weight="fill" className="size-3.5" />
              </span>
              <CardEyebrow>{section.title}</CardEyebrow>
            </div>

            <div className="flex flex-col gap-2">
              {section.variants.map((variant) => {
                const href = buildHref(section.route, variant.query);
                return (
                  <Link
                    key={`${section.route}-${variant.label}`}
                    to={href}
                    className="group bg-card/90 supports-backdrop-filter:bg-card/85 ring-foreground/10 hover:ring-foreground/25 hover:bg-card relative flex flex-col gap-1.5 overflow-hidden rounded-2xl p-4 ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-[15px] sm:text-base">
                        {variant.label}
                      </CardTitle>
                      <Badge tone="neutral">
                        <ArrowRightIcon weight="bold" className="size-3" />
                      </Badge>
                    </div>
                    {variant.description && (
                      <p className="text-muted-foreground text-[12px] leading-relaxed">
                        {variant.description}
                      </p>
                    )}
                    <code className="text-muted-foreground/70 mt-1 block break-all font-mono text-[10px] leading-relaxed">
                      {href}
                    </code>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Card>
        <p className="text-muted-foreground text-[12px] leading-relaxed">
          Pra sair do preview:{' '}
          <code className="rounded bg-foreground/10 px-1 py-0.5">
            localStorage.removeItem(&apos;devPreview&apos;)
          </code>{' '}
          no console + remova{' '}
          <code className="rounded bg-foreground/10 px-1 py-0.5">
            VITE_DEV_PREVIEW
          </code>{' '}
          de <code className="rounded bg-foreground/10 px-1 py-0.5">packages/webapp/.env.local</code>.
        </p>
      </Card>
    </ScreenShell>
  );
}
